import React, { useEffect, useState } from "react";
import ModalShell from "./ModalShell";
import { IconDelete, iconBtnStyle } from "../shared/icons/IconLib";
import {
  RENTAL_LEASE_TENANTS_MAX,
  normalizeTenant,
  normalizeTenantsList,
  parseTenantQuickAddLine,
  validateTenantsList,
} from "../shared/utils/rentalLeaseTenants";

/**
 * Lease tenants modal — read-only by default; Edit unlocks quick-add, inline edit, and row delete.
 * Quick-add: "Name, email, phone" (email uppercased on add).
 */
export default function LeaseTenantsModal({
  open,
  onClose,
  tenants = [],
  onSave,
  saving = false,
  title = "Tenants",
}) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [quickAdd, setQuickAdd] = useState("");
  const [error, setError] = useState("");

  function loadFromProps() {
    const list = normalizeTenantsList(tenants);
    setRows(list.length > 0 ? list.map(normalizeTenant) : []);
    setQuickAdd("");
    setError("");
  }

  useEffect(() => {
    if (!open) return;
    setEditing(false);
    loadFromProps();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when modal opens / source tenants change
  }, [open, tenants]);

  function startEdit() {
    loadFromProps();
    if (normalizeTenantsList(tenants).length === 0) {
      setRows([{ full_name: "", email: "", phone: "" }]);
    }
    setEditing(true);
    setError("");
  }

  function cancelEdit() {
    loadFromProps();
    setEditing(false);
  }

  function handleClose() {
    if (saving) return;
    setEditing(false);
    if (onClose) onClose();
  }

  function updateRow(idx, key, value) {
    setRows((prev) =>
      prev.map((t, i) => {
        if (i !== idx) return t;
        return { ...t, [key]: value != null ? String(value) : "" };
      })
    );
  }

  function blurEmail(idx) {
    setRows((prev) => prev.map((t, i) => (i === idx ? normalizeTenant(t) : t)));
  }

  function removeRow(idx) {
    setRows((prev) => {
      if (prev.length <= 1) {
        setError("At least one tenant is required");
        return prev;
      }
      setError("");
      return prev.filter((_, i) => i !== idx);
    });
  }

  function handleQuickAdd(e) {
    e?.preventDefault();
    if (!editing) return;
    const parsed = parseTenantQuickAddLine(quickAdd);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    if (rows.filter((t) => t.full_name).length >= RENTAL_LEASE_TENANTS_MAX) {
      setError(`Too many tenants (max ${RENTAL_LEASE_TENANTS_MAX})`);
      return;
    }
    setError("");
    setRows((prev) => {
      const cleaned = prev.filter((t) => t.full_name || t.email || t.phone);
      return [...cleaned, parsed.tenant];
    });
    setQuickAdd("");
  }

  async function handleSave() {
    const validated = validateTenantsList(rows);
    if (!validated.ok) {
      setError(validated.error);
      return;
    }
    setError("");
    if (!onSave) {
      setEditing(false);
      return;
    }
    try {
      await onSave(validated.tenants);
      setRows(validated.tenants);
      setEditing(false);
    } catch (err) {
      setError(err?.message || "Failed to save tenants");
    }
  }

  const subtitle = editing
    ? "Add tenants with Name, email, phone. Edit inline; delete from each row."
    : "Tenant details (read-only). Click Edit to add or change tenants.";

  return (
    <ModalShell
      open={open}
      onClose={saving ? undefined : handleClose}
      title={title}
      subtitle={subtitle}
      maxWidth={720}
      zIndex={1120}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, width: "100%" }}>
          {!editing ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={saving}>
                Close
              </button>
              <button type="button" className="btn btn-primary" onClick={startEdit} disabled={saving}>
                Edit
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={cancelEdit} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      }
    >
      {editing ? (
        <form onSubmit={handleQuickAdd} style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          <label className="panel-label" htmlFor="lease-tenant-quick-add">
            Quick add (Name, email, phone)
          </label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              id="lease-tenant-quick-add"
              type="text"
              className="input input-full"
              value={quickAdd}
              onChange={(e) => setQuickAdd(e.target.value)}
              placeholder="Chimere Crump, cacrump24@gmail.com, (469) 990-8172"
              disabled={saving}
              autoFocus
            />
            <button type="submit" className="btn btn-secondary" disabled={saving || !quickAdd.trim()}>
              Add
            </button>
          </div>
          <div className="panel-hint">Email is stored in UPPERCASE when added.</div>
        </form>
      ) : null}

      {error ? (
        <div className="panel-hint" style={{ color: "#b02a37", marginBottom: 10 }}>
          {error}
        </div>
      ) : null}

      <div style={{ display: "grid", gap: 10 }}>
        {rows.length === 0 && !editing ? (
          <div className="panel-hint">No tenants yet. Click Edit to add one.</div>
        ) : null}

        {rows.map((t, idx) => (
          <div key={idx} className="panel" style={{ padding: 12 }}>
            {editing ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 2fr 1.5fr auto",
                  gap: 10,
                  alignItems: "end",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label className="panel-label">Full name</label>
                  <input
                    type="text"
                    className="input input-full"
                    value={t.full_name}
                    onChange={(e) => updateRow(idx, "full_name", e.target.value)}
                    disabled={saving}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label className="panel-label">Email</label>
                  <input
                    type="email"
                    className="input input-full"
                    value={t.email}
                    onChange={(e) => updateRow(idx, "email", e.target.value)}
                    onBlur={() => blurEmail(idx)}
                    disabled={saving}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label className="panel-label">Phone</label>
                  <input
                    type="text"
                    className="input input-full"
                    value={t.phone}
                    onChange={(e) => updateRow(idx, "phone", e.target.value)}
                    disabled={saving}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", paddingBottom: 2 }}>
                  <button
                    type="button"
                    className="btn-link-danger"
                    style={iconBtnStyle}
                    title="Delete tenant"
                    aria-label="Delete tenant"
                    onClick={() => removeRow(idx)}
                    disabled={saving || rows.length <= 1}
                  >
                    <IconDelete size={18} />
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 2fr 1.5fr",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <div className="panel-label">Full name</div>
                  <div style={{ fontWeight: 500, wordBreak: "break-word" }}>{t.full_name || "—"}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <div className="panel-label">Email</div>
                  <div style={{ wordBreak: "break-word" }}>{t.email || "—"}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <div className="panel-label">Phone</div>
                  <div style={{ wordBreak: "break-word" }}>{t.phone || "—"}</div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </ModalShell>
  );
}
